import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { SlidersHorizontal } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { GalleryEditor, galleryFromPaths } from '../../features/GalleryEditor/index.js';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { MeasurementsForm } from '../../shared/ui/MeasurementsForm/MeasurementsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import {
  buildEntityFormData,
  mapLocalizationsFromApi,
} from '../../shared/utils/buildEntityFormData.js';

const LIST_PATH = '/shop/products';

const LOCALIZATION_FIELDS = [
  { name: 'title', label: 'Название', type: 'text', required: true },
  { name: 'description', label: 'Описание', type: 'textarea', rows: 4 },
  // Значение — путь своей обложки (только для превью), файл уходит полем localization_{LANG}_mainImg
  {
    name: '_mainImgPath',
    label: 'Обложка',
    type: 'image',
    entityType: 'product',
    clearable: false,
  },
  {
    name: 'photos',
    label: 'Галерея',
    type: 'gallery',
    hint: 'Своя галерея перевода. Удалённые фото исчезнут после сохранения.',
  },
  { name: 'seoMetatags', label: 'SEO', type: 'seo' },
];

// Галереи: в JSON — итоговый список (ImgPath или имя поля нового файла photo_{guid} / localization_{LANG}_photo_{guid})
const GALLERY_FIELDS = [{ name: 'photos', formField: 'photo' }];

const emptySeo = { title: '', description: '', keywords: '' };

const emptyForm = {
  title: '',
  description: '',
  sku: '',
  categoryId: 0,
  basePricing: '',
  measurements: {
    WeightInGramms: 0,
    HeightInMillimeters: 0,
    LengthInMillimeters: 0,
    WidthInMillimeters: 0,
  },
  seoMetatags: emptySeo,
  photos: [],
};

// API отдаёт габариты в camelCase, MeasurementsForm работает с PascalCase
const mapMeasurements = (m) => ({
  ...(m?.id ? { id: m.id } : {}),
  WeightInGramms: m?.weightInGramms ?? 0,
  HeightInMillimeters: m?.heightInMillimeters ?? 0,
  LengthInMillimeters: m?.lengthInMillimeters ?? 0,
  WidthInMillimeters: m?.widthInMillimeters ?? 0,
});

const photoPaths = (entity) => entity?.photos ?? (entity?.images || []).map((i) => i.imgPath);

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Создание и редактирование товара: основные поля, обложка, галерея, SEO и локализации — одним multipart-запросом
 */
const ProductFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [mainImgPath, setMainImgPath] = useState('');
  const [localizations, setLocalizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const applyProduct = (product) => {
    const productImgPath = product.mainImage?.imgPath || '';
    setMainImgPath(productImgPath);
    setForm({
      id: product.id,
      title: product.title || '',
      description: product.description || '',
      sku: product.sku || '',
      categoryId: product.categoryId,
      basePricing: product.basePricing ?? '',
      measurements: mapMeasurements(product.measurements),
      seoMetatags: product.seoMetatags || emptySeo,
      photos: galleryFromPaths(photoPaths(product)),
    });
    setLocalizations(
      mapLocalizationsFromApi(product.localizations, []).map((l) => {
        const ownImgPath = l.mainImage?.imgPath;
        return {
          id: l.id,
          language: l.language,
          title: l.title || '',
          description: l.description || '',
          useLocalizationImages: l.useLocalizationImages ?? true,
          // Без своей обложки у локализации хранится копия основной — тогда показываем «своей нет»
          _mainImgPath: ownImgPath && ownImgPath !== productImgPath ? ownImgPath : '',
          photos: galleryFromPaths(photoPaths(l)),
          seoMetatags: l.seoMetatags || emptySeo,
        };
      }),
    );
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [categoriesRes, product] = await Promise.all([
          apiRequests.products.getCategories(200),
          isEdit ? apiRequests.products.getById(id) : Promise.resolve(null),
        ]);
        const categoryItems = categoriesRes.items || [];
        setCategories(categoryItems);

        if (product) {
          applyProduct(product);
        } else {
          setForm({ ...emptyForm, categoryId: categoryItems[0]?.id || 0 });
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Не удалось загрузить данные товара'));
      }
    };
    load();
  }, [id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!form.title.trim()) {
      toast.error('Введите название товара');
      return;
    }
    if (!form.categoryId) {
      toast.error('Выберите категорию — без неё товар не сохранить');
      return;
    }
    if (!isEdit && !mainImage) {
      toast.error('Загрузите обложку товара');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const model = { ...form, basePricing: Number(form.basePricing) || 0 };
      const body = buildEntityFormData({
        model,
        mainImage,
        contentKeys: [],
        localizations,
        galleryFields: GALLERY_FIELDS,
      });

      if (isEdit) {
        await apiRequests.products.update(body);
        toast.success('Товар сохранён');
        // Новые фото получили пути на сервере — перечитываем товар, чтобы повторное сохранение не загрузило их снова
        setMainImage(null);
        applyProduct(await apiRequests.products.getById(id));
      } else {
        await apiRequests.products.create(body);
        toast.success('Товар создан');
        setTimeout(() => navigate(LIST_PATH), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить товар'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate(LIST_PATH)}
      saving={saving}
      submitText={isEdit ? 'Сохранить изменения' : 'Создать товар'}
      savingText={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <Row>
              <Col md={8}>
                <FormField label="Название" required>
                  <Form.Control
                    type="text"
                    value={form.title}
                    onChange={(e) => setField('title', e.target.value)}
                    placeholder="Например, Худи Alfateam"
                  />
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Артикул">
                  <Form.Control
                    type="text"
                    value={form.sku}
                    onChange={(e) => setField('sku', e.target.value)}
                    placeholder="SKU"
                  />
                </FormField>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <FormField label="Категория" required>
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setField('categoryId', Number(e.target.value))}
                  >
                    {!categories.length && <option value={0}>Сначала создайте категорию</option>}
                    {categories.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.title}
                      </option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Базовая цена" hint="Без учёта модификаторов">
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.basePricing}
                      onChange={(e) => setField('basePricing', e.target.value)}
                      placeholder="0"
                    />
                    <InputGroup.Text>$</InputGroup.Text>
                  </InputGroup>
                </FormField>
              </Col>
            </Row>

            <FormField label="Описание">
              <Form.Control
                as="textarea"
                rows={4}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </FormField>

            {isEdit && (
              <Link
                to={`${LIST_PATH}/${id}/modifiers`}
                className="d-inline-flex align-items-center gap-2 small"
              >
                <SlidersHorizontal size={14} />
                Модификаторы товара (размер, цвет и т.п.)
              </Link>
            )}
          </Col>

          <Col lg={4}>
            <FormField label="Обложка" required={!isEdit}>
              <UploadPicture
                onSelect={setMainImage}
                initialValue={mainImgPath || undefined}
                entityType="product"
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Галерея">
        <FormField hint="Дополнительные фото на странице товара. Удалённые фото исчезнут после сохранения.">
          <GalleryEditor value={form.photos} onChange={(photos) => setField('photos', photos)} />
        </FormField>
      </FormSection>

      <FormSection title="Габариты">
        <MeasurementsForm
          title={null}
          measurements={form.measurements}
          onChange={(measurements) => setField('measurements', measurements)}
        />
      </FormSection>

      <FormSection title="SEO">
        <SEOMetatagsForm
          seoMetatags={form.seoMetatags}
          onChange={(seo) => setField('seoMetatags', seo)}
        />
      </FormSection>

      <LocalizationsBlock
        value={localizations}
        onChange={setLocalizations}
        fields={LOCALIZATION_FIELDS}
        source={form}
        aiContext="товар интернет-магазина"
      />
    </FormLayout>
  );
};

export default ProductFormPage;
